import { ArrowRight, BarChart3, CalendarDays, Camera, Check, Clock3, MapPinned, Sparkles, Video, Volume2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Badge, Button, Card } from '../../components/ui'
import { useToast } from '../../components/ui/toastContext'
import { CARAVAN_TRIP } from '../../config/trip'
import { getChallengeResponse, getTripDayProgram } from '../../config/tripChallenges'
import { imageForStage } from '../../config/tripMedia'
import type { AgendaEvent } from '../../data/local/agendaRepository'
import type { ChallengeCompletion, ChallengeView } from '../../data/local/challengeRepository'
import type { PollView } from '../../data/local/pollRepository'
import { dailyPhotoPhase } from '../../domain/dailyPhoto'
import { triggerCelebration } from '../../domain/celebration'
import { DEFAULT_DARIJA_ENTRIES, selectDailyDarijaGroup, type DarijaEntry } from '../../domain/darija'
import { journeyHeroLine, journeyMoment } from '../../domain/journeyState'
import { tripDateKey } from '../../domain/tripTime'
import { speakDarijaEntry } from '../../services/darijaSpeech'
import { agendaService } from '../agenda/agendaService'
import { sessionStore } from '../access/sessionStore'
import { useAdminView } from '../access/useAdminView'
import { albumRepository } from '../album/albumService'
import { challengeRepository } from '../group/challengeService'
import { pollRepository } from '../group/pollService'
import { NotificationCenter } from './NotificationCenter'
import { NotificationReminder } from './NotificationReminder'
import { displayedChallengeProductDay, isDaySixPreview } from '../../domain/challengePreview'
import { DailyPhotoCapture } from './DailyPhotoCapture'
import { selectNowAction } from './nowAction'

function shortStageDates(startsOn: string, endsOn: string): string {
  const format = (value: string) => new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', timeZone: CARAVAN_TRIP.timeZone }).format(new Date(`${value}T12:00:00Z`))
  return `${format(startsOn)} – ${format(endsOn)}`
}

function emptyDayMessage(productDay: string): string {
  if (productDay === CARAVAN_TRIP.startsOn) return 'Arrivée à Marrakech. Le premier rendez-vous du jour arrive demain.'
  if (productDay === CARAVAN_TRIP.endsOn) return 'Dernier jour au Maroc.'
  return 'Les rendez-vous quotidiens sont proposés du 6 au 18 septembre.'
}

export function TodayPage() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const session = sessionStore((state) => state.session)
  const isAdminView = useAdminView()
  const [now, setNow] = useState(() => new Date())
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false)
  const [todayEvents, setTodayEvents] = useState<AgendaEvent[]>([])
  const [todayPolls, setTodayPolls] = useState<PollView[]>([])
  const [albumSummary, setAlbumSummary] = useState({ group: 0, own: 0 })
  const [todayChallenge, setTodayChallenge] = useState<ChallengeView | null>(null)
  const [completions, setCompletions] = useState<ChallengeCompletion[]>([])
  const [groupChallengeCount, setGroupChallengeCount] = useState(0)
  const [todayDarija, setTodayDarija] = useState<readonly DarijaEntry[]>(() => selectDailyDarijaGroup(DEFAULT_DARIJA_ENTRIES, tripDateKey(now)))

  const moment = journeyMoment(now)
  const productDay = moment.productDay
  const challengeProductDay = displayedChallengeProductDay(now)
  const previewing = isDaySixPreview(now)
  const program = getTripDayProgram(challengeProductDay)
  const stage = moment.phase === 'transition' ? moment.nextStage : moment.stage
  const heroImage = imageForStage(stage.name, stage.order)
  const photoPhase = program ? dailyPhotoPhase(challengeProductDay, now) : 'outside_trip'
  const ownChallengeDone = Boolean(todayChallenge && completions.some((item) => item.challengeId === todayChallenge.id && item.memberId === session?.memberId))
  const nextEvent = todayEvents[0] ?? null

  const refreshDay = useCallback(() => {
    if (!session) return
    const freshNow = new Date()
    const day = displayedChallengeProductDay(freshNow)
    void Promise.all([
      agendaService.list(),
      pollRepository.listPollViews(session.memberId, isAdminView),
      challengeRepository.listChallengesFor(session.memberId, isAdminView, freshNow),
      challengeRepository.listCompletionsFor(session.memberId, freshNow),
      albumRepository.listPhotos(session.tripId),
    ]).then(([events, polls, challenges, challengeCompletions, photos]) => {
      setTodayEvents(events.filter((event) => event.date === tripDateKey(freshNow)).slice(0, 4))
      setTodayPolls(polls.filter((poll) => poll.status === 'open' || poll.status === 'revealed').slice(0, 3))
      const nextChallenge = challenges.find((item) => item.productDay === day && item.contentRole === 'daily_challenge') ?? null
      setTodayChallenge(nextChallenge)
      if (nextChallenge) void challengeRepository.countCompletionsFor(nextChallenge.id).then(setGroupChallengeCount)
      else setGroupChallengeCount(0)
      setCompletions(challengeCompletions)
      const candidates = photos.filter((photo) => photo.purpose === 'daily_candidate' && photo.productDay === day)
      setAlbumSummary({ group: candidates.length, own: candidates.filter((photo) => photo.memberId === session.memberId).length })
    })
  }, [isAdminView, session])

  useEffect(() => {
    refreshDay()
    const timer = window.setInterval(() => { setNow(new Date()); refreshDay() }, 60_000)
    return () => window.clearInterval(timer)
  }, [refreshDay])

  useEffect(() => {
    void import('../../services/syncRuntime').then(({ practicalRepository }) => practicalRepository.loadDarijaEntries()).then((entries) => {
      setTodayDarija(selectDailyDarijaGroup(entries, tripDateKey(new Date())))
    })
  }, [])

  async function completeChallenge() {
    if (!session || !todayChallenge || !program || todayChallenge.phase !== 'active') return
    try {
      const completion = await challengeRepository.complete({
        tripId: session.tripId,
        challengeId: todayChallenge.id,
        memberId: session.memberId,
        productDay: challengeProductDay,
        text: 'Réalisé',
        photoId: null,
        createdAtDevice: new Date().toISOString(),
      })
      refreshDay()
      triggerCelebration('challenge')
      showToast({
        message: 'Défi marqué comme réalisé.',
        tone: 'success',
        actionLabel: 'Annuler',
        durationMs: 6_000,
        onAction: () => void challengeRepository.undoPendingCompletion(completion.id, session.memberId).then((undone) => {
          if (undone) { refreshDay(); showToast({ message: 'Réalisation annulée.', tone: 'success' }) }
        }),
      })
    } catch (caught) {
      showToast({ message: caught instanceof Error ? caught.message : 'Le défi n’a pas pu être marqué.', tone: 'error' })
    }
  }

  const challengeNeedsForm = todayChallenge ? Boolean((getChallengeResponse(todayChallenge.id)?.fields.length ?? 0) > 0 || getChallengeResponse(todayChallenge.id)?.video) : false
  const beforeTrip = moment.phase === 'before'
  const photoAction = photoPhase === 'open' && albumSummary.own === 0
  const resultsAvailable = photoPhase === 'revealed'
  const openPoll = todayPolls.find((poll) => poll.status === 'open' && !poll.hasVoted) ?? null
  const revealedPoll = todayPolls.find((poll) => poll.status === 'revealed') ?? null
  const nowAction = selectNowAction({
    beforeTrip,
    photoDue: program?.media.kind === 'photo' && photoAction,
    photoDone: albumSummary.own > 0,
    challengeDue: todayChallenge?.phase === 'active' && !ownChallengeDone,
    challengeDone: ownChallengeDone,
    pollDue: openPoll !== null,
    appointmentNear: nextEvent !== null,
    revealAvailable: resultsAvailable || revealedPoll !== null,
  })

  const nowContent = nowAction === 'photo'
    ? { icon: Camera, title: 'Ajoute ta photo du jour', detail: program?.media.title ?? '', label: 'Ajouter ma photo', action: () => setPhotoCaptureOpen(true) }
    : nowAction === 'challenge'
      ? { icon: Sparkles, title: 'Passe au défi du jour', detail: albumSummary.own > 0 ? 'Photo ajoutée. Il te reste le défi.' : program?.challenge.title ?? '', label: challengeNeedsForm ? 'Répondre' : 'Voir le défi', action: () => void navigate('/groupe') }
      : nowAction === 'poll'
        ? { icon: BarChart3, title: openPoll?.title ?? 'Un vote est ouvert', detail: 'Ton choix prend quelques secondes.', label: 'Voter', action: () => void navigate('/groupe') }
        : nowAction === 'appointment'
          ? { icon: Clock3, title: nextEvent?.title ?? 'Arrivée à Marrakech', detail: nextEvent ? `${nextEvent.time} · ${nextEvent.place || 'Lieu à préciser'}` : '5 septembre', label: 'Voir l’agenda', action: () => void navigate('/agenda') }
          : nowAction === 'reveal'
            ? { icon: Sparkles, title: 'La révélation est disponible', detail: 'On peut maintenant découvrir les participations.', label: 'Ouvrir', action: () => void navigate(resultsAvailable ? '/souvenirs' : '/groupe') }
            : nowAction === 'complete'
              ? { icon: Check, title: 'C’est fait pour toi', detail: 'On ouvre tout à 22 h.', label: 'Voir la journée', action: () => void navigate('/groupe') }
              : null
  const NowIcon = nowContent?.icon ?? Clock3

  return (
    <main className="page today-page">
      <header className="today-heading">
        <div><p className="eyebrow">5–19 SEPTEMBRE · MAROC</p><h1>Bonjour {session?.displayName}</h1></div>
        <div className="today-heading__actions"><NotificationCenter /></div>
      </header>

      <NotificationReminder />

      {nowContent ? <Card className={`today-now today-now--${nowAction ?? 'calm'}`} aria-labelledby="today-now-title">
        <span className="today-now__icon" aria-hidden="true"><NowIcon size={22} /></span>
        <div><p className="eyebrow">MAINTENANT</p><h2 id="today-now-title">{nowContent.title}</h2><p>{nowContent.detail}</p></div>
        <Button onClick={nowContent.action}>{nowContent.label} <ArrowRight size={17} /></Button>
      </Card> : null}

      <section className="today-hero" aria-label="Étape du voyage au Maroc">
        <img src={heroImage.path} alt={heroImage.alt} />
        <div className="today-hero__veil" />
        <div className="today-hero__copy">
          <p>{journeyHeroLine(moment).toLocaleUpperCase('fr')}</p>
          <h2>{stage.name}</h2>
          <span>{shortStageDates(stage.startsOn, stage.endsOn)}</span>
          <Button aria-label="Voir cette étape sur la carte" variant="ghost" onClick={() => void navigate('/carte')}><MapPinned size={17} /> Voir la carte</Button>
        </div>
      </section>

      <section className="today-heart" aria-labelledby="today-heart-title">
        <div className="section-title"><div><p className="eyebrow">LE CŒUR DU JOUR</p><h2 id="today-heart-title">{beforeTrip ? 'Premier rendez-vous le 6 septembre' : 'À vous de jouer'}</h2></div></div>

        <Card className={`today-focus-card today-focus-card--photo${resultsAvailable ? ' is-revealed' : ''}`} data-today-section="photo">
          <span className="today-photo-visual" aria-hidden="true">
            <Camera size={29} />
            <i /><i /><i /><i />
            <small>{previewing ? 'JOUR 2' : productDay.slice(-2)}</small>
          </span>
          <div>
            <p className="eyebrow">{previewing ? 'APERÇU DU 6 SEPTEMBRE · ' : ''}{program?.media.kind === 'video' ? 'VIDÉO DU JOUR' : 'PHOTO DU JOUR'}</p>
            <h2>{program?.media.title ?? 'Pas de thème aujourd’hui'}</h2>
            <p>{program?.media.detail ?? emptyDayMessage(productDay)}</p>
            {program && !previewing && albumSummary.own > 0 ? <span className="today-progress">C’est fait · {String(albumSummary.group)}/6 ont participé{photoPhase === 'open' ? ' · on ouvre tout à 22 h' : ''}</span> : null}
          </div>
          {program && previewing ? <Button disabled variant="secondary">Disponible le 6 septembre</Button> : null}
          {program && photoAction && program.media.kind === 'photo' ? <Button variant="secondary" onClick={() => setPhotoCaptureOpen(true)}><Camera size={18} /> Ajouter ma photo</Button> : null}
          {program && photoPhase === 'open' && albumSummary.own > 0 ? <Button variant="secondary" onClick={() => void navigate('/souvenirs')}><Check size={18} /> Photo ajoutée</Button> : null}
          {program && resultsAvailable ? <Button variant="secondary" onClick={() => void navigate('/souvenirs')}>Voir les photos <ArrowRight size={17} /></Button> : null}
          {program && !previewing && program.media.kind === 'video' ? <Button variant="secondary" onClick={() => void navigate('/groupe')}><Video size={18} /> Ajouter la vidéo</Button> : null}
        </Card>

        <Card className="today-focus-card today-focus-card--challenge" data-today-section="challenge">
          <span className="today-focus-card__icon"><Sparkles size={23} /></span>
          <div>
            <p className="eyebrow">{previewing ? 'APERÇU DU 6 SEPTEMBRE · ' : ''}DÉFI DU JOUR</p>
            <h2>{program?.challenge.title ?? 'Pas de défi aujourd’hui'}</h2>
            <p>{program?.challenge.detail ?? emptyDayMessage(productDay)}</p>
            {program && !previewing && ownChallengeDone ? <span className="today-progress">C’est fait · {String(groupChallengeCount)}/6 ont participé · on ouvre tout à 22 h</span> : null}
          </div>
          {program && previewing ? <Button disabled variant="secondary">Disponible le 6 septembre</Button> : null}
          {program && !previewing && todayChallenge?.phase === 'locked' ? <Button disabled variant="secondary">Disponible à 02:00</Button> : null}
          {program && !previewing && todayChallenge?.phase === 'active' && !ownChallengeDone && !challengeNeedsForm ? <Button variant="secondary" onClick={() => void completeChallenge()}><Check size={18} /> Marquer comme réalisé</Button> : null}
          {program && !previewing && todayChallenge?.phase === 'active' && !ownChallengeDone && challengeNeedsForm ? <Button variant="secondary" onClick={() => void navigate('/groupe')}>Répondre <ArrowRight size={17} /></Button> : null}
          {program && !previewing && ownChallengeDone ? <span className="today-done"><Check size={17} /> Réalisé</span> : null}
          {program && !previewing && todayChallenge?.phase === 'group_revealed' ? <Button variant="secondary" onClick={() => void navigate('/groupe')}>Voir les résultats <ArrowRight size={17} /></Button> : null}
        </Card>
      </section>

      <Card className="today-next-event" data-today-section="next-event">
        <span><Clock3 size={22} /></span>
        <div><p className="eyebrow">PROCHAIN RENDEZ-VOUS</p><h2>{nextEvent?.title ?? (beforeTrip ? 'Arrivée à Marrakech' : 'La journée reste libre')}</h2><p>{nextEvent ? `${nextEvent.time} · ${nextEvent.place || 'Lieu à préciser'}` : beforeTrip ? '5 septembre' : 'Ajoutez un rendez-vous si besoin.'}</p></div>
        <Button aria-label="Ouvrir l’agenda" variant="secondary" onClick={() => void navigate('/agenda')}><CalendarDays size={18} /> Agenda</Button>
      </Card>

      {todayPolls.length > 0 ? <section className="today-polls" aria-label="Votes actifs et révélations récentes">
        <div className="section-title"><div><p className="eyebrow">CHOIX DU GROUPE</p><h2>Votes actifs</h2></div><Button aria-label="Ouvrir les votes" variant="ghost" onClick={() => void navigate('/groupe')}><ArrowRight size={18} /></Button></div>
        {todayPolls.map((poll) => <Card className="today-poll-card" key={poll.id}><BarChart3 size={18} /><span><strong>{poll.title}</strong><small>{poll.status === 'revealed' ? 'Résultat disponible' : `${String(poll.options.length)} choix`}</small></span><Badge tone={poll.status === 'revealed' ? 'warning' : 'success'}>{poll.status === 'revealed' ? 'Résultat' : 'En cours'}</Badge></Card>)}
      </section> : null}

      <Card className="daily-card daily-card--darija" aria-labelledby="today-darija-title">
        <header><div><p className="eyebrow">DARIJA DU JOUR</p><h2 id="today-darija-title">Les mots utiles du jour</h2></div><Button variant="ghost" onClick={() => void navigate('/pratique')}>Tout le lexique <ArrowRight size={16} /></Button></header>
        <div className="today-darija-list">{todayDarija.map((entry) => <div className="today-darija-entry" key={entry.id}><span className="arabic" lang="ar" dir="rtl">{entry.expression}</span><span><strong>{entry.transcription}</strong><small>{entry.meaning}</small></span><Button aria-label={`Écouter ${entry.transcription}`} className="today-darija-entry__audio" variant="ghost" onClick={() => speakDarijaEntry(entry)}><Volume2 size={18} /></Button></div>)}</div>
      </Card>

      {photoCaptureOpen && session ? <DailyPhotoCapture productDay={challengeProductDay} session={session} onAdded={refreshDay} onClose={() => setPhotoCaptureOpen(false)} /> : null}
    </main>
  )
}
